/**
 * useAvatarUpload Hook
 * 
 * Centralized hook for uploading and syncing avatars across all databases.
 * 
 * Flow:
 * 1. Upload image to backend (which uploads to Supabase Storage using service key)
 * 2. Backend syncs to Supabase profiles, Supabase Auth, and MongoDB
 * 3. Frontend syncs to Firestore
 * 4. Update local cache
 * 5. Return the new avatar URL
 * 
 * Usage:
 *   const { uploadAvatar, uploading, error } = useAvatarUpload();
 *   const newUrl = await uploadAvatar(user.id, file, user.name);
 */

import { useState, useCallback } from 'react';
import { updateUserAvatar } from '@/lib/firestore';
import { useAvatarCache } from '@/contexts';
import { uploadWithProgress } from '@/lib/uploadVideoWithProgress';
import { isImageFile } from '@/lib/imageFiles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Validates an image file before upload
 * @param {File} file - The file to validate
 * @returns {{ valid: boolean, error?: string }}
 */
function validateImageFile(file) {
  // Check file type (HEIC may arrive with an empty type on Windows; the server converts it)
  if (!isImageFile(file)) {
    return { valid: false, error: 'Please select an image file' };
  }
  
  // Check file size (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    return { valid: false, error: 'Image File Size too big. Must be under 5MB' };
  }
  
  // Check file extension
  const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'hif'];
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (!extension || !validExtensions.includes(extension)) {
    return { valid: false, error: 'Invalid file type. Use JPG, PNG, GIF, WebP, or HEIC' };
  }
  
  return { valid: true };
}

/**
 * Hook for uploading avatars with full sync across all databases
 */
export function useAvatarUpload() {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  
  // Get cache functions for updating after upload
  const { updateAvatar: updateCachedAvatar } = useAvatarCache();
  
  /**
   * Upload avatar and sync across all databases
   * @param {string} userId - The user's ID
   * @param {File} file - The image file to upload
   * @param {string} userName - The user's display name (for Firestore sync)
   * @returns {Promise<string|null>} The new avatar URL, or null on failure
   */
  const uploadAvatar = useCallback(async (userId, file, userName) => {
    setUploading(true);
    setError(null);
    setProgress(0);
    
    try {
      // Step 1: Validate the file (fast, no progress update needed —
      // progress will jump to >0% on the first XHR byte event anyway)
      const validation = validateImageFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }
      
      // Step 2: Upload via backend API with REAL XHR byte progress.
      // uploadWithProgress reserves the top 10% (90→100) for server-side
      // work (backend re-uploads to Supabase Storage + writes profile rows).
      // The XHR's onprogress events drive 0→90 based on actual bytes sent.
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', userId);
      if (userName) {
        formData.append('name', userName);
      }
      
      const result = await uploadWithProgress(
        `${API_URL}/api/avatar/upload`,
        formData,
        (pct) => setProgress(pct),
        10, // 10% reserved for server-side processing
      );
      // After uploadWithProgress resolves, progress is at 100.
      // We deliberately hold it at 100 (don't reset to a lower number) while
      // Firestore sync runs below — the sync is typically <200ms and
      // displaying it as part of the "upload completing" tail is honest.
      
      const avatarUrl = result.avatar_url;
      console.log('Upload successful:', avatarUrl);
      
      // Step 3: Sync to Firestore (update user document + all conversations)
      try {
        await updateUserAvatar(userId, avatarUrl, userName);
      } catch (firestoreError) {
        console.error('Firestore sync error:', firestoreError);
        // Don't throw - avatar is uploaded, Firestore will sync on next message
      }
      
      // Step 4: Update local cache
      updateCachedAvatar(userId, avatarUrl);
      
      // Step 5: Clear stale localStorage caches that might have old avatar URLs
      try {
        localStorage.removeItem('cache_recentComms');
        localStorage.removeItem(`userProfile_${userId}`);
        console.log('Cleared localStorage caches after avatar update');
      } catch (e) {
        console.warn('Failed to clear localStorage caches:', e);
      }
      
      return { success: true, avatarUrl };
      
    } catch (err) {
      console.error('Avatar upload error:', err);
      const errorMessage = err.message || 'Failed to upload avatar';
      setError(errorMessage);
      // Return an object with error info so caller can access it immediately
      return { success: false, error: errorMessage };
    } finally {
      setUploading(false);
    }
  }, [updateCachedAvatar]);
  
  /**
   * Delete old avatars for a user (cleanup) - not implemented yet
   */
  const cleanupOldAvatars = useCallback(async (userId) => {
    // Cleanup is handled server-side
    console.log('Cleanup requested for user:', userId);
  }, []);
  
  return {
    uploadAvatar,
    cleanupOldAvatars,
    uploading,
    error,
    progress
  };
}

export default useAvatarUpload;
